import type { ReceiptDetailDto } from '@pos/types';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';

import { Btn } from '../../components/Btn';
import { Card } from '../../components/Card';
import { EmptyState } from '../../components/EmptyState';
import { FilterBar } from '../../components/FilterBar';
import { Input } from '../../components/Input';
import { Modal } from '../../components/Modal';
import { DataTable } from '../../components/Table';
import { dashboardApi } from '../../lib/apiClient';
import { fmtBaht, fmtDate } from '../../lib/format';
import { useBranchScope } from '../../lib/useBranchScope';
import { useDateRange } from '../../lib/useDateRange';
import { useSession } from '../../stores/session';
import { ReportHeader } from './ReportHeader';
import { TaxInvoiceModal } from './TaxInvoiceModal';

export function ReceiptHistoryView({
  mode = 'history',
}: {
  mode?: 'history' | 'tax-invoice';
}) {
  const isTaxInvoiceMode = mode === 'tax-invoice';
  const { customFromIso, customToIso, setCustom } = useDateRange();
  const { scope } = useBranchScope();
  const userId = useSession((state) => state.user?.id ?? 'anonymous');
  const [from, setFrom] = useState(customFromIso);
  const [to, setTo] = useState(customToIso);
  const [receiptNo, setReceiptNo] = useState('');
  const [selectedReceiptNo, setSelectedReceiptNo] = useState<string | null>(
    null,
  );
  const [taxInvoiceOpen, setTaxInvoiceOpen] = useState(false);
  const [mobileDetailOpen, setMobileDetailOpen] = useState(false);

  useEffect(() => {
    setSelectedReceiptNo(null);
    setTaxInvoiceOpen(false);
    setMobileDetailOpen(false);
  }, [isTaxInvoiceMode]);

  const branchId = scope.branchId || undefined;
  const receiptsQuery = useQuery({
    queryKey: [
      'receipts',
      userId,
      from,
      to,
      branchId,
      receiptNo,
      isTaxInvoiceMode,
    ],
    queryFn: () =>
      dashboardApi.listReceipts({
        from,
        to,
        branchId,
        receiptNo: receiptNo.trim() || undefined,
        taxInvoiceIssuedOnly: isTaxInvoiceMode,
        limit: 50,
      }),
  });
  const detailQuery = useQuery({
    queryKey: ['receipt-detail', userId, selectedReceiptNo, branchId, from, to],
    enabled: !!selectedReceiptNo,
    queryFn: () =>
      dashboardApi.getReceipt({
        receiptNo: selectedReceiptNo!,
        branchId,
        from,
        to,
      }),
  });

  const receipts = receiptsQuery.data?.receipts ?? [];
  const selected = detailQuery.data?.receipt;
  const visibleTotal = receipts.reduce(
    (sum, receipt) => sum + receipt.totalAmount,
    0,
  );

  const selectReceipt = (nextReceiptNo: string) => {
    setSelectedReceiptNo(nextReceiptNo);
    setTaxInvoiceOpen(false);
    setMobileDetailOpen(
      typeof window !== 'undefined' &&
        typeof window.matchMedia === 'function' &&
        window.matchMedia('(max-width: 1023px)').matches,
    );
  };

  return (
    <div className="bo-fade space-y-6">
      <ReportHeader
        title={isTaxInvoiceMode ? 'ใบกำกับภาษี' : 'ประวัติใบเสร็จ'}
        subtitle={
          isTaxInvoiceMode
            ? 'แสดงเฉพาะบิลที่ออกใบกำกับภาษีแล้ว'
            : 'ค้นหา ตรวจสอบ และดูรายละเอียดใบเสร็จย้อนหลัง'
        }
        status={{
          label: receiptsQuery.isLoading
            ? 'กำลังค้นหา'
            : `พบ ${receipts.length} ${isTaxInvoiceMode ? 'ใบกำกับภาษี' : 'ใบเสร็จ'}`,
          tone: receiptsQuery.isLoading ? 'neutral' : 'ok',
        }}
      />

      <FilterBar>
        <div className="w-full">
          <div className="mb-4 flex flex-col justify-between gap-2 sm:flex-row sm:items-end">
            <div>
              <h2
                id="receipt-search-heading"
                className="text-h3 font-bold text-ink"
              >
                ค้นหาเอกสาร
              </h2>
              <p className="mt-1 text-body text-ink-3">
                ระบุช่วงวันที่ หรือใช้เลขที่เอกสารเพื่อค้นหาโดยตรง
              </p>
            </div>
            {!receiptsQuery.isLoading && receipts.length > 0 && (
              <div className="text-left sm:text-right">
                <div className="text-sm-label text-ink-3">
                  มูลค่ารวมรายการที่แสดง
                </div>
                <strong className="num text-h3 text-ink">
                  {fmtBaht(visibleTotal)}
                </strong>
                {receiptsQuery.data?.hasMore && (
                  <div className="mt-1 text-sm-label text-ink-3">
                    แสดง {receipts.length} จาก {receiptsQuery.data.total} รายการ
                  </div>
                )}
              </div>
            )}
          </div>
          <div className="grid gap-3 md:grid-cols-[160px_160px_minmax(0,1fr)_auto] md:items-end">
            <label className="grid min-w-0 gap-2 text-sm-label font-bold text-ink-2">
              จากวันที่
              <Input
                aria-label="จากวันที่"
                type="date"
                value={from}
                onChange={(e) => setFrom(e.target.value)}
              />
            </label>
            <label className="grid min-w-0 gap-2 text-sm-label font-bold text-ink-2">
              ถึงวันที่
              <Input
                aria-label="ถึงวันที่"
                type="date"
                value={to}
                onChange={(e) => setTo(e.target.value)}
              />
            </label>
            <label className="grid min-w-0 gap-2 text-sm-label font-bold text-ink-2">
              เลขที่ใบเสร็จ
              <Input
                aria-label="เลขที่ใบเสร็จ"
                placeholder="เช่น RCP-0001"
                value={receiptNo}
                onChange={(e) => setReceiptNo(e.target.value)}
              />
            </label>
            <Btn
              className="min-h-11 w-full md:w-auto"
              onClick={() => {
                setCustom(from, to);
                void receiptsQuery.refetch();
              }}
            >
              ค้นหา
            </Btn>
          </div>
        </div>
      </FilterBar>

      <div className="grid gap-4 lg:grid-cols-[minmax(0,1fr)_420px]">
        <section
          aria-labelledby="receipt-results-heading"
          className="min-w-0 space-y-3"
        >
          <div>
            <h2
              id="receipt-results-heading"
              className="text-h2 font-bold text-ink"
            >
              {isTaxInvoiceMode ? 'ใบกำกับภาษีที่ออกแล้ว' : 'รายการใบเสร็จ'}
            </h2>
            <p className="mt-1 text-body text-ink-3">
              เลือกรายการเพื่อดูสินค้า การชำระเงิน และข้อมูลภาษี
            </p>
          </div>
          <Card className="overflow-hidden">
            {receiptsQuery.isLoading ? (
              <div className="p-6 text-ink-3">กำลังโหลด...</div>
            ) : receipts.length === 0 ? (
              <EmptyState
                title={isTaxInvoiceMode ? 'ไม่พบใบกำกับภาษี' : 'ไม่พบใบเสร็จ'}
                sub={
                  isTaxInvoiceMode
                    ? 'ยังไม่มีบิลที่ออกใบกำกับภาษีในช่วงวันที่นี้'
                    : 'ลองเปลี่ยนช่วงวันที่หรือเลขที่ใบเสร็จ'
                }
              />
            ) : (
              <DataTable
                pageSize={Number.MAX_SAFE_INTEGER}
                mobileCards
                data={receipts}
                onRowClick={(receipt) => selectReceipt(receipt.receiptNo)}
                columns={[
                  { accessorKey: 'receiptNo', header: 'เลขที่' },
                  {
                    id: 'date',
                    header: 'วันที่',
                    cell: ({ row }) => fmtDate(new Date(row.original.soldAt)),
                  },
                  { accessorKey: 'branchId', header: 'สาขา' },
                  {
                    id: 'promotions',
                    header: 'โปรโมชัน',
                    cell: ({ row }) =>
                      `${row.original.appliedPromotions.length} รายการ`,
                  },
                  {
                    accessorKey: 'totalAmount',
                    header: 'ยอดรวม',
                    cell: ({ row }) => fmtBaht(row.original.totalAmount),
                  },
                  {
                    id: 'copy',
                    header: isTaxInvoiceMode ? 'ออกเมื่อ' : 'สำเนา',
                    cell: ({ row }) =>
                      isTaxInvoiceMode && row.original.taxInvoiceIssuedAt
                        ? fmtDate(new Date(row.original.taxInvoiceIssuedAt))
                        : row.original.copyCount,
                  },
                ]}
              />
            )}
          </Card>
        </section>

        <Card className="hidden self-start p-4 lg:block lg:sticky lg:top-4">
          <ReceiptDetailPanel
            isLoading={detailQuery.isLoading}
            isTaxInvoiceMode={isTaxInvoiceMode}
            receipt={selected}
            selectedReceiptNo={selectedReceiptNo}
            onTaxInvoice={() => setTaxInvoiceOpen(true)}
          />
        </Card>
      </div>

      <Modal
        variant="drawer"
        open={mobileDetailOpen}
        title={isTaxInvoiceMode ? 'รายละเอียดใบกำกับภาษี' : 'รายละเอียดใบเสร็จ'}
        description={
          selectedReceiptNo ? `เลขที่ ${selectedReceiptNo}` : undefined
        }
        onClose={() => setMobileDetailOpen(false)}
        className="!my-0 lg:hidden"
      >
        <ReceiptDetailPanel
          isLoading={detailQuery.isLoading}
          isTaxInvoiceMode={isTaxInvoiceMode}
          receipt={selected}
          selectedReceiptNo={selectedReceiptNo}
          onTaxInvoice={() => setTaxInvoiceOpen(true)}
        />
      </Modal>

      {selected && (
        <TaxInvoiceModal
          open={taxInvoiceOpen}
          receipt={selected}
          onClose={() => setTaxInvoiceOpen(false)}
          onIssued={() => {
            void receiptsQuery.refetch();
            void detailQuery.refetch();
          }}
        />
      )}
    </div>
  );
}

function ReceiptDetailPanel({
  selectedReceiptNo,
  receipt,
  isLoading,
  isTaxInvoiceMode,
  onTaxInvoice,
}: {
  selectedReceiptNo: string | null;
  receipt?: ReceiptDetailDto;
  isLoading: boolean;
  isTaxInvoiceMode: boolean;
  onTaxInvoice: () => void;
}) {
  if (!selectedReceiptNo) {
    return (
      <EmptyState
        title="เลือกใบเสร็จ"
        sub={
          isTaxInvoiceMode
            ? 'เลือกรายการเพื่อดูหรือพิมพ์ใบกำกับภาษีอีกครั้ง'
            : 'รายละเอียดจะแสดงด้านนี้'
        }
      />
    );
  }
  if (isLoading) {
    return (
      <div className="py-8 text-center text-ink-3">กำลังโหลดรายละเอียด...</div>
    );
  }
  if (!receipt) return <EmptyState title="ไม่พบรายละเอียด" />;

  return (
    <div className="min-w-0 space-y-4">
      <div className="rounded-card bg-bg-warm p-4">
        <div className="text-sm-label text-ink-3">ใบเสร็จ</div>
        <div className="break-all text-h3 font-bold tabnum">
          {receipt.receiptNo}
        </div>
        <div className="mt-1 text-body text-ink-3">
          {fmtDate(new Date(receipt.soldAt))} · {receipt.branchId}
        </div>
      </div>
      <div>
        <h3 className="text-body font-bold text-ink">รายการสินค้า</h3>
        <div className="mt-2 divide-y divide-line">
          {receipt.lines.map((line) => (
            <div
              key={line.id}
              className="flex min-w-0 items-start justify-between gap-3 py-3"
            >
              <div className="min-w-0">
                <div className="break-words font-semibold">
                  {line.productName ?? line.productId}
                </div>
                <div className="text-sm-label text-ink-3 tabnum">
                  {line.quantity} × {fmtBaht(line.unitPrice)}
                </div>
              </div>
              <div className="shrink-0 tabnum">{fmtBaht(line.amount)}</div>
            </div>
          ))}
        </div>
      </div>
      {receipt.appliedPromotions.length > 0 && (
        <div className="rounded-card bg-status-success/5 p-3">
          <div className="mb-2 font-semibold text-status-success">
            โปรโมชันที่ใช้
          </div>
          <div className="space-y-2">
            {receipt.appliedPromotions.map((promotion) => (
              <div
                key={promotion.id}
                className="flex min-w-0 items-start justify-between gap-3 text-body"
              >
                <div className="min-w-0 break-words">
                  <div>{promotion.name}</div>
                  {promotion.detail && (
                    <div className="text-sm-label text-ink-3">
                      {promotion.detail}
                    </div>
                  )}
                  {promotion.freeQuantity !== undefined &&
                    promotion.freeQuantity > 0 && (
                      <div className="text-sm-label text-ink-3">
                        ของแถม {promotion.freeQuantity} ชิ้น
                      </div>
                    )}
                </div>
                <div className="shrink-0 tabnum text-status-success">
                  -{fmtBaht(promotion.discountAmount)}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
      {(receipt.billDiscount ?? 0) > 0 && (
        <DetailAmount
          label="ส่วนลดท้ายบิล"
          value={`-${fmtBaht(receipt.billDiscount ?? 0)}`}
          tone="warn"
        />
      )}
      {(receipt.roundingDelta ?? 0) !== 0 && (
        <DetailAmount
          label="ยอดปัดเศษ"
          value={`${(receipt.roundingDelta ?? 0) > 0 ? '+' : ''}${fmtBaht(receipt.roundingDelta ?? 0)}`}
        />
      )}
      <div className="flex items-center justify-between gap-3 border-t border-line pt-4 text-h3 font-bold">
        <span>รวม</span>
        <span className="num shrink-0">{fmtBaht(receipt.totalAmount)}</span>
      </div>
      <div className="break-words text-sm-label text-ink-3">
        ชำระเงิน:{' '}
        {receipt.payments
          .map((payment) => payment.paymentLabel?.trim() || payment.method)
          .join(', ') || '—'}
      </div>
      <Btn
        className="min-h-11 w-full sm:w-auto"
        kind="primary"
        onClick={onTaxInvoice}
      >
        {isTaxInvoiceMode ? 'พิมพ์ใบกำกับภาษีอีกครั้ง' : 'ออกใบกำกับภาษี'}
      </Btn>
    </div>
  );
}

function DetailAmount({
  label,
  value,
  tone = 'default',
}: {
  label: string;
  value: string;
  tone?: 'default' | 'warn';
}) {
  return (
    <div
      className={`flex items-center justify-between gap-3 rounded-card p-3 text-body ${
        tone === 'warn' ? 'bg-warn-light text-warn' : 'bg-bg-warm text-ink'
      }`}
    >
      <span className="font-semibold">{label}</span>
      <span className="num shrink-0">{value}</span>
    </div>
  );
}
